import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaAward, FaCalendarAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { ProfileDetails } from '../components/professionals/ProfileDetails';
import { ShareProfileLink } from '../components/professionals/ShareProfileLink';

const TYPE_LABELS = {
  financial_advisor: 'Financial Advisor', estate_attorney: 'Estate Attorney', cpa_tax: 'CPA & Tax Expert',
  real_estate: 'Real Estate Expert', insurance: 'Insurance Specialist', family_therapist: 'Family Therapist',
};

export default function ProfessionalProfile() {
  const { profileId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pro, setPro] = useState(undefined);

  useEffect(() => {
    axios.get(`/api/directory/professionals/${profileId}`).then(r => setPro(r.data)).catch(() => setPro(null));
  }, [profileId]);

  useEffect(() => {
    if (pro) document.title = `${pro.name} · Legacy Forge Family`;
  }, [pro]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="professional-profile-page">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/"><BrandLogo className="h-10 w-10" /></Link>
          <Link to="/professionals/directory" className="text-sm text-blue-700 hover:underline flex items-center" data-testid="profile-back-to-directory">
            <FaArrowLeft className="mr-1" />All professionals
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        {pro === undefined && <p className="text-gray-500">Loading…</p>}
        {pro === null && (
          <div className="bg-white rounded-xl p-8 text-center shadow" data-testid="profile-not-found">
            <h1 className="text-xl font-bold text-gray-800 mb-2">This profile isn't available</h1>
            <p className="text-gray-600">The professional may no longer be listed.</p>
          </div>
        )}
        {pro && (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="profile-name">{pro.name}</h1>
              <p className="text-blue-100">{TYPE_LABELS[pro.professional_type] || 'Professional'}</p>
              {pro.company && <p className="text-blue-200 text-sm">{pro.company}</p>}
              {pro.founding_advisor && (
                <span className="mt-2 inline-flex items-center gap-1 bg-amber-400/20 text-amber-200 text-xs font-bold px-3 py-1 rounded-full">
                  <FaAward /> Founding Advisor
                </span>
              )}
              {pro.pilot_participant && (
                <span className="mt-2 ml-2 inline-flex items-center gap-1 bg-emerald-400/20 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full" data-testid="profile-pilot-badge">
                  <FaAward /> Founding Pilot Professional
                </span>
              )}
            </div>
            <div className="p-6">
              <ProfileDetails professional={pro} onReported={() => navigate('/professionals/directory')} />
              <ShareProfileLink profileId={pro.profile_id} showOpen={false} />
              <button
                onClick={() => navigate(user ? '/resources?tab=professionals' : '/register')}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center"
                data-testid="profile-book-btn"
              >
                <FaCalendarAlt className="mr-2" />{user ? 'Book Consultation' : 'Sign Up to Book'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
